/**
 * Textos (`ca`) de les fitxes tècniques de proveïdor, fase 1 (OBR-41): adjuntar-les, sol·licitar-les i la vista de
 * «FT pendents per proveïdor». Tall propi del Catàleg: només s'edita aquest fitxer (el cablejat és a `cataleg/ca.ts`).
 * Les tres llengües han de tenir les mateixes claus (i18n.test.ts); cap text literal a les vistes.
 *
 * `emailSubjectDefault` i `emailBodyDefault` són les PLANTILLES del correu: es renderitzen en l'idioma del proveïdor
 * (el de les comandes, i si no en té, el de qui sol·licita) i la persona les pot editar abans d'enviar-les. La llista
 * d'articles, el comiat i la identitat de l'obrador els afegeix el servidor.
 */

export const ftRequestCa = {
  'backoffice.ftRequest': {
    // ── Estat ────────────────────────────────────────────────────────────────────────────
    statusNone: 'Sense FT',
    statusRequested: 'Sol·licitada',
    statusValid: 'Vigent',

    // ── El bloc d'una relació article-proveïdor ──────────────────────────────────────────
    blockTitle: 'Fitxa tècnica del proveïdor',
    noneHint: 'Encara no s’ha adjuntat ni sol·licitat la fitxa tècnica d’aquest proveïdor.',
    requestedSummary: 'Sol·licitada el {{date}} a {{recipient}}.',
    requestedSummaryNoRecipient: 'Sol·licitada el {{date}}.',
    validDocument: 'Document del {{date}} · versió {{version}}',
    validAttached: 'Incorporada el {{date}} per {{name}}.',
    validAttachedNoName: 'Incorporada el {{date}}.',
    legacyHint:
      'Adjuntada abans de registrar-ne la data i la versió: no es coneixen. Adjunta-la de nou o completa-les per deixar-ne constància.',
    openFile: 'Obre la FT',
    attachAction: 'Adjunta FT',
    replaceAction: 'Substitueix FT',
    completeAction: 'Completa data i versió',
    requestAction: 'Sol·licita FT',
    requestAgainAction: 'Torna a sol·licitar',
    saveFirstHint: 'Desa l’article per adjuntar o sol·licitar la FT d’aquest proveïdor.',
    supplierChangedHint:
      'Has canviat el proveïdor d’aquesta relació: desa l’article abans d’adjuntar o sol·licitar la FT.',
    supplierInactiveHint: 'Aquest proveïdor està de baixa: no se li pot sol·licitar la FT.',
    loadError: 'No s’ha pogut llegir l’estat de les FT d’aquest article.',

    // ── Adjuntar ─────────────────────────────────────────────────────────────────────────
    attachTitle: 'Adjunta la FT de {{supplier}}',
    attachIntro:
      'La fitxa tècnica del proveïdor per a {{article}}. Cal la data que porta el document i la seva versió.',
    fieldFile: 'Fitxer (PDF)',
    fieldFileHint: 'PDF de fins a 10 MB.',
    keepFile: 'Mantén el fitxer actual i només corregeix la data i la versió',
    fieldDocumentDate: 'Data del document',
    fieldDocumentDateHint: 'La que porta la fitxa del proveïdor, no la d’avui.',
    fieldVersion: 'Versió',
    fieldVersionHint: 'Tal com la indica el proveïdor (p. ex. «v3» o «2026-05»).',
    attachConfirm: 'Adjunta',
    attachError: 'No s’ha pogut adjuntar la FT. Torna-ho a provar.',
    attachForbidden: 'No tens permís per adjuntar fitxes tècniques.',
    attachNotFound: 'Aquesta relació article-proveïdor ja no existeix. Recarrega la pàgina.',
    attachSuccess: 'FT adjuntada.',
    attachSaved: 'Dades de la FT desades.',
    errorFileRequired: 'Tria el fitxer PDF de la FT.',
    errorDateRequired: 'Escriu la data del document.',
    errorDateInvalid: 'La data del document no és vàlida.',
    errorDateFuture: 'La data del document no pot ser futura.',
    errorVersionRequired: 'Escriu la versió del document.',
    errorVersionTooLong: 'La versió és massa llarga (màxim {{max}} caràcters).',

    // ── Sol·licitar ──────────────────────────────────────────────────────────────────────
    requestTitle: 'Sol·licita la FT a {{supplier}}',
    requestIntro:
      'S’enviarà un correu des d’Obrava al proveïdor. Pots editar l’assumpte i el text; la llista d’articles s’hi afegeix sola.',
    recipientLabel: 'Destinatari',
    recipientQuality: '{{email}} (correu de qualitat)',
    recipientGeneral: '{{email}} (correu general: el proveïdor no té correu de qualitat)',
    noRecipientWarning:
      'Aquest proveïdor no té cap correu electrònic. Afegeix-ne un a la seva fitxa (Configuració · Proveïdors) per poder-li sol·licitar la FT: no s’enviarà res.',
    fieldSubject: 'Assumpte',
    fieldBody: 'Text',
    articlesLabel: 'Articles inclosos ({{count}})',
    articleLine: '{{name}}',
    articleLineRef: '{{name}} · ref. {{ref}}',
    requestConfirm: 'Envia la sol·licitud',
    requestSuccess: 'Sol·licitud enviada a {{recipient}}.',
    errorNoRecipient:
      'Aquest proveïdor no té cap correu electrònic: no s’ha enviat res. Afegeix-ne un a la seva fitxa.',
    errorNoArticles: 'Marca almenys un article.',
    errorTooManyArticles: 'Una petició admet com a màxim {{max}} articles.',
    errorSubjectRequired: 'Escriu l’assumpte.',
    errorSubjectTooLong: 'L’assumpte és massa llarg (màxim {{max}} caràcters).',
    errorBodyRequired: 'Escriu el text del missatge.',
    errorBodyTooLong: 'El text és massa llarg (màxim {{max}} caràcters).',
    errorForbidden: 'No tens permís per sol·licitar fitxes tècniques.',
    errorSupplierNotFound: 'No s’ha trobat el proveïdor.',
    errorArticlesNotLinked:
      'Algun dels articles ja no és d’aquest proveïdor. Tanca la finestra, revisa la llista i torna-ho a provar.',
    errorAlreadyHasFt:
      'Algun dels articles ja té la FT adjuntada. Tanca la finestra, revisa la llista i torna-ho a provar.',
    errorSmtpNotConfigured: 'L’enviament de correu no està configurat. Avisa l’administrador d’Obrava.',
    errorRegistryUnavailable:
      'El registre de sol·licituds no està disponible: no s’ha enviat res. Avisa l’administrador d’Obrava.',
    errorRecentlyRequested:
      'Ja s’ha sol·licitat la FT a aquest proveïdor fa poc: no se li pot tornar a escriure fins passats uns minuts.',
    errorDailyLimit:
      'S’ha arribat al límit de sol·licituds de FT de les últimes 24 hores. Torna-ho a provar més tard.',
    limitCooldown:
      'Ja s’ha sol·licitat la FT a aquest proveïdor fa poc. Perquè no rebi correus repetits, no se li pot tornar a escriure fins d’aquí a {{minutes}} min.',
    limitDaily:
      'L’obrador ha arribat al límit de {{max}} sol·licituds de FT en 24 hores. Es podrà tornar a sol·licitar d’aquí a {{hours}} h.',
    fileBadUrl: 'L’adreça del fitxer no és vàlida.',
    errorSendFailed: 'No s’ha pogut enviar el correu. No s’ha registrat res: pots tornar-ho a provar.',
    errorRecordFailed:
      'El correu s’ha enviat, però no s’ha pogut registrar la sol·licitud. No el tornis a enviar.',
    errorTryLater: 'No s’ha pogut contactar amb el servidor. Torna-ho a provar d’aquí a un moment.',
    errorGeneric: 'No s’ha pogut enviar la sol·licitud.',

    // ── La plantilla del correu (es renderitza en l'idioma del proveïdor) ────────────────
    emailSubjectDefault: 'Sol·licitud de fitxa tècnica · {{obrador}}',
    emailBodyDefault:
      'Bon dia,\n\nPer completar el nostre registre de matèries primeres i garantir-ne la traçabilitat, necessitem la fitxa tècnica vigent dels articles següents. Us agrairem que responeu aquest correu adjuntant-la en PDF, amb la data i la versió del document.\n\nMoltes gràcies.',

    // ── FT pendents per proveïdor ────────────────────────────────────────────────────────
    pendingButton: 'FT pendents',
    pendingTitle: 'FT pendents per proveïdor',
    pendingIntro:
      'Els articles que encara no tenen la fitxa tècnica del proveïdor, agrupats per proveïdor. Cada proveïdor rep una sola petició amb tots els articles que marquis.',
    pendingTotals: 'Proveïdors: {{suppliers}} · Articles sense FT: {{articles}}',
    pendingLoading: 'Carregant les FT pendents…',
    pendingLoadError: 'No s’han pogut carregar les FT pendents.',
    pendingEmpty: 'No hi ha cap FT pendent: tots els articles amb proveïdor tenen la fitxa tècnica.',
    colArticle: 'Article',
    colRef: 'Ref. proveïdor',
    colStatus: 'Estat',
    groupMeta: 'Sense FT: {{count}}',
    groupNoRecipient: 'Sense correu',
    groupNoRecipientHint:
      'Aquest proveïdor no té cap correu electrònic: afegeix-ne un a la seva fitxa per poder-li sol·licitar la FT.',
    groupRecipient: 'A: {{recipient}}',
    groupRequestAction: 'Sol·licita FT ({{count}})',
    groupSelectNone: 'Marca almenys un article per sol·licitar-lo.',
    groupSelectAll: 'Marca tots els articles de {{supplier}}',
    itemSelect: 'Inclou {{article}} a la petició',
    itemRequestedOn: 'Sol·licitada el {{date}}',
    pendingClose: 'Tanca',
  },
} as const;
